import { useCallback, useMemo, useState, type ReactNode } from 'react';

import type { CallPrefill } from '../calls/CallPop';
import type { OrderDetail } from '../orders/api';
import { DispatchContext, type DispatchState } from './dispatchContext';

/**
 * Holds what every dispatch view needs, above the routes, so that switching view keeps the
 * selected order, the call pop and the last confirmation.
 */
export function DispatchProvider({ children }: { children: ReactNode }) {
  const [selectedOrder, setSelectedOrder] = useState<OrderDetail | null>(null);
  const [fromCall, setFromCall] = useState<CallPrefill | null>(null);
  const [lastTaken, setLastTaken] = useState<string | null>(null);
  const [orderOnCall, setOrderOnCall] = useState<{ callId: string; orderId: string } | null>(null);
  const [popped, setPopped] = useState<string[]>([]);
  const [driverToShow, showDriver] = useState<string | null>(null);

  const onCreated = useCallback(
    (order: OrderDetail) => {
      setLastTaken(order.reference);
      setOrderOnCall(fromCall === null ? null : { callId: fromCall.callId, orderId: order.id });

      // The order just taken is the one the dispatcher wants to watch next — who is
      // offered it, who takes it — so it opens as it is created, selected in the queue,
      // rather than being found there by reference a moment later.
      setSelectedOrder(order);
    },
    [fromCall],
  );

  const takeCall = useCallback((prefill: CallPrefill | null) => {
    setFromCall(prefill);
    setOrderOnCall(null);
  }, []);

  const state = useMemo<DispatchState>(
    () => ({
      selectedOrder,
      setSelectedOrder,
      fromCall,
      takeCall,
      popped,
      setPopped,
      orderOnCall,
      onCreated,
      lastTaken,
      driverToShow,
      showDriver,
    }),
    [selectedOrder, fromCall, takeCall, orderOnCall, onCreated, lastTaken, popped, driverToShow],
  );

  return <DispatchContext.Provider value={state}>{children}</DispatchContext.Provider>;
}
